import {
  resolveMetricValue,
  unknownResult,
  type GoalMetricInput,
  type GoalValue,
  type MetricInput,
  type MetricResult,
  type NodeMetrics,
  type TimeMetricInput,
  type TimeValue,
} from './contracts';
import { conversion as conversionFormula } from './formulas/conversion';
import { cpl as cplFormula } from './formulas/cpl';
import { cac as cacFormula } from './formulas/cac';
import { averageTicket as averageTicketFormula } from './formulas/revenue';

export function manualNumber(input: MetricInput | undefined): number | null {
  return input?.mode === 'manual' && typeof input.manualValue === 'number' ? input.manualValue : null;
}

export interface NodeFlowContext {
  /** Entries as resolved by graph propagation (merge of incoming edge flows), or a manual override. */
  entries: number | null;
  entriesOrigin: 'manual' | 'calculated' | null;
}

export interface NodeMetricsResult {
  entries: MetricResult;
  exits: MetricResult;
  conversion: MetricResult;
  cost: MetricResult;
  revenue: MetricResult;
  cpl: MetricResult;
  cac: MetricResult;
  averageTicket: MetricResult;
  averageTime: MetricResult<TimeValue>;
  goal: MetricResult<GoalValue>;
}

/**
 * SPEC-008 §11/§39/§40/plan §11/§12 — resolves exits/conversion together:
 * whichever of the two is manual anchors the other's calculated candidate
 * (§39: entries × conversion → exits; §16: exits / entries → conversion),
 * without ever overwriting a manual value (METRIC-REQ-013).
 */
export function resolveExitsAndConversion(
  entries: number | null,
  metrics: NodeMetrics,
): { exits: MetricResult; conversion: MetricResult } {
  const manualExits = manualNumber(metrics.exits);
  const manualConversion = manualNumber(metrics.conversion);

  if (manualExits !== null) {
    // Exits manual wins (METRIC-REQ-013); conversion's calculated candidate
    // is the pure display derivation exits/entries (§16).
    const convCandidate = conversionFormula(entries, manualExits);
    // If the user also set a manual conversion rate, entries × that rate is
    // still a useful calculated candidate for exits, purely for display —
    // it never overrides the manual exits value (METRIC-REQ-004/013).
    const exitsCandidate =
      manualConversion !== null && entries !== null ? entries * manualConversion : null;
    return {
      exits: resolveMetricValue(metrics.exits, exitsCandidate),
      conversion: resolveMetricValue(metrics.conversion, convCandidate.value, convCandidate.warnings, convCandidate.explanation),
    };
  }

  if (manualConversion !== null && entries !== null) {
    const exitsCandidate = entries * manualConversion;
    const exitsExplanation = {
      metric: 'exits',
      formula: 'entries * conversion',
      inputs: { entries, conversion: manualConversion },
      result: exitsCandidate,
    };
    return {
      exits: resolveMetricValue(metrics.exits, exitsCandidate, [], exitsExplanation),
      conversion: resolveMetricValue(metrics.conversion, null, [], undefined),
    };
  }

  // Neither anchors the other: exits stays whatever resolveMetricValue says
  // with no calculated candidate; conversion likewise has nothing to derive
  // from yet (entries known but no exits anchor at all).
  return {
    exits: resolveMetricValue(metrics.exits, null),
    conversion: resolveMetricValue(metrics.conversion, null),
  };
}

function resolveTime(input: TimeMetricInput | undefined): MetricResult<TimeValue> {
  if (input?.mode === 'manual' && input.manualValue) {
    return {
      status: 'known',
      effectiveValue: input.manualValue,
      calculatedCandidate: null,
      origin: 'manual',
      warnings: [],
    };
  }
  return unknownResult<TimeValue>();
}

function resolveGoal(input: GoalMetricInput | undefined): MetricResult<GoalValue> {
  if (input?.manualValue) {
    return {
      status: 'known',
      effectiveValue: input.manualValue,
      calculatedCandidate: null,
      origin: 'manual',
      warnings: [],
    };
  }
  return unknownResult<GoalValue>();
}

/**
 * SPEC-008 §11/plan §11 — evaluates every node-scoped metric for one node,
 * given its already-graph-resolved entries. Cost/revenue/averageTime/goal
 * never depend on graph structure; CPL/CAC/averageTicket here are the
 * node-scoped versions (§21/§23/§24), distinct from the funnel-level
 * aggregates in `aggregation/funnel-metrics.ts`.
 */
export function evaluateNodeMetrics(metrics: NodeMetrics, flow: NodeFlowContext): NodeMetricsResult {
  const entriesResult = resolveMetricValue(metrics.entries, flow.entries, [], undefined);
  const effectiveEntries = flow.entries;

  const { exits, conversion } = resolveExitsAndConversion(effectiveEntries, metrics);
  const effectiveExits = exits.effectiveValue as number | null;

  const cost = resolveMetricValue(metrics.cost, null);
  const revenue = resolveMetricValue(metrics.revenue, null);

  const cplCandidate = cplFormula(cost.effectiveValue as number | null, effectiveEntries);
  const cpl = resolveMetricValue(metrics.cpl, cplCandidate.value, cplCandidate.warnings, cplCandidate.explanation);

  const cacCandidate = cacFormula(cost.effectiveValue as number | null, effectiveExits);
  const cac = resolveMetricValue(metrics.cac, cacCandidate.value, cacCandidate.warnings, cacCandidate.explanation);

  const ticketCandidate = averageTicketFormula(revenue.effectiveValue as number | null, effectiveExits);
  const averageTicket = resolveMetricValue(
    metrics.averageTicket,
    ticketCandidate.value,
    ticketCandidate.warnings,
    ticketCandidate.explanation,
  );

  return {
    entries: entriesResult,
    exits,
    conversion,
    cost,
    revenue,
    cpl,
    cac,
    averageTicket,
    averageTime: resolveTime(metrics.averageTime),
    goal: resolveGoal(metrics.goal),
  };
}
